import { useTranslation } from 'next-i18next'
import { useEffect, useRef, useState } from 'react'
import { v4 as uuid } from 'uuid'

import ActivityWrapper from '~/bootcamp/Activities/components/ActivityWrapper'
import Equals from '~/bootcamp/Activities/components/Equals'
import Input from '~/bootcamp/Activities/components/Input'
import FractionsActivityWrapper from '~/bootcamp/Activities/Fractions/components/FractionsActivityWrapper'
import { ActivityProps } from '~/bootcamp/Activities/types/activities.types'
import Divider from '~/bootcamp/Activities/components/Divider'
import useFocus from '~/bootcamp/Activities/hooks/useFocus'
import { randomIntFromInterval, hocOnEnterPress } from '~/bootcamp/Activities/utils/activities.utils'
import { useEmitInstance } from '~/bootcamp/Activities/utils/instance.utils'

const MINIMUM_NUMBER = [1, 20]
const MAXIMUM_NUMBER = [20, 50]
const MAX_HISTORY_LENGTH = [10, 15]

function FractionInteger({
  answerState,
  onNextQuestionClick,
  onAnswerStateChange,
  onInstanceChange,
  actionButtonsFC: SubmitButton,
  autofocus = true,
  difficultyLevel,
}: ActivityProps): React.ReactElement {
  const { t } = useTranslation('generics')
  const [answer, setAnswer] = useState({ denominator: '', numerator: '' })
  const [fractionValue, setFractionValue] = useState<number>(0)
  const [taskHistory, setTaskHistory] = useState<number[]>([])
  const [inputKey, setInputKey] = useState<string>(uuid())

  const inputRef = useRef<HTMLInputElement>(null)
  useFocus(autofocus, answerState, inputRef)

  useEmitInstance(onInstanceChange, fractionValue ? `${fractionValue} = \\frac{\\square}{\\square}` : null)

  useEffect(() => {
    setAnswer({ denominator: '', numerator: '' })
    setFractionValue(
      randomIntFromInterval(MINIMUM_NUMBER[difficultyLevel - 1], MAXIMUM_NUMBER[difficultyLevel - 1], taskHistory),
    )
  }, [taskHistory, difficultyLevel])

  useEffect(() => {
    onAnswerStateChange(null)
  }, [answer, onAnswerStateChange])

  const handleOnCheckAnswerClick = (): void => {
    const isCorrect = answer.numerator === fractionValue.toString() && answer.denominator === '1'
    onAnswerStateChange(isCorrect)
  }

  const resetAnswer = (): void => {
    setAnswer({
      denominator: '',
      numerator: '',
    })
  }

  const handleOnNextQuestionClick = (): void => {
    setTaskHistory((prevState) => {
      return [fractionValue, ...prevState].slice(0, MAX_HISTORY_LENGTH[difficultyLevel])
    })
    onAnswerStateChange(null)
    onNextQuestionClick()
    setInputKey(uuid())
  }
  const onEnterPress = (): void => {
    if (answer.denominator !== '' && answer.numerator !== '') {
      hocOnEnterPress(answerState, handleOnCheckAnswerClick, handleOnNextQuestionClick)
    }
  }

  return (
    <ActivityWrapper>
      <FractionsActivityWrapper>
        {fractionValue}
        <Equals />
        <div className="ml-4 flex flex-col items-center">
          <Input
            aria-label={t('ariaLabelButtons.insertNumerator')}
            autoFocus={autofocus}
            isError={answerState === false}
            key={`${inputKey}-first`}
            maxDigits={4}
            onChange={(value) => setAnswer((state) => ({ denominator: state.denominator, numerator: value }))}
            onEnter={onEnterPress}
            ref={inputRef}
            value={answer.numerator}
          />
          <Divider className="my-2" />
          <Input
            aria-label={t('ariaLabelButtons.insertDenominator')}
            isError={answerState === false}
            key={`${inputKey}-second`}
            maxDigits={4}
            onChange={(value) => setAnswer((state) => ({ denominator: value, numerator: state.numerator }))}
            onEnter={onEnterPress}
            value={answer.denominator}
          />
        </div>
      </FractionsActivityWrapper>
      {SubmitButton && (
        <SubmitButton
          clearFormAction={() => resetAnswer()}
          correctAnswer={answerState ?? false}
          formValid={answer.denominator !== '' && answer.numerator !== ''}
          goToNextQuestionAction={handleOnNextQuestionClick}
          submitAnswerAction={handleOnCheckAnswerClick}
        />
      )}
    </ActivityWrapper>
  )
}

export default FractionInteger
